import moment from 'moment';
import 'moment/locale/tr'; // Turkish locale

/**
 * Moment.js based date formatting utilities
 * Provides dd/MM/yyyy format for both display and input
 */

// Set Turkish locale as default
moment.locale('tr');

/**
 * Format date to dd/MM/yyyy format using moment.js
 * @param date - Date string, Date object, or moment object
 * @returns Formatted date string in dd/MM/yyyy format
 */
export const formatDateWithMoment = (date: string | Date | moment.Moment): string => {
  try {
    if (!date) return '';
    
    const momentDate = moment(date);
    if (!momentDate.isValid()) return '';
    
    return momentDate.format('DD/MM/YYYY');
  } catch (error) {
    console.warn('Moment date formatting error:', error);
    return '';
  }
};

/**
 * Format date for HTML date input (YYYY-MM-DD format)
 * @param date - Date string, Date object, or moment object
 * @returns Formatted date string in YYYY-MM-DD format for HTML date inputs
 */
export const formatDateForInput = (date: string | Date | moment.Moment): string => {
  try {
    if (!date) return '';
    
    const momentDate = moment(date);
    if (!momentDate.isValid()) return '';
    
    return momentDate.format('YYYY-MM-DD');
  } catch (error) {
    console.warn('Moment date input formatting error:', error);
    return '';
  }
};

/**
 * Parse date from dd/MM/yyyy format to moment object
 * @param dateString - Date string in dd/MM/yyyy format
 * @returns Moment object or null if invalid
 */
export const parseDateFromDisplay = (dateString: string): moment.Moment | null => {
  try {
    if (!dateString) return null;
    
    const momentDate = moment(dateString, 'DD/MM/YYYY', true);
    if (!momentDate.isValid()) return null;
    
    return momentDate;
  } catch (error) {
    console.warn('Moment date parsing error:', error);
    return null;
  }
};

/**
 * Parse date from HTML date input (YYYY-MM-DD format) to moment object
 * @param dateString - Date string in YYYY-MM-DD format
 * @returns Moment object or null if invalid
 */
export const parseDateFromInput = (dateString: string): moment.Moment | null => {
  try {
    if (!dateString) return null;
    
    const momentDate = moment(dateString, 'YYYY-MM-DD', true);
    if (!momentDate.isValid()) return null;
    
    return momentDate;
  } catch (error) {
    console.warn('Moment date input parsing error:', error);
    return null;
  }
};

/**
 * Get current date in dd/MM/yyyy format
 * @returns Current date formatted as dd/MM/yyyy
 */
export const getCurrentDateFormatted = (): string => {
  return moment().format('DD/MM/YYYY');
};

/**
 * Get current date in YYYY-MM-DD format for HTML date inputs
 * @returns Current date formatted as YYYY-MM-DD
 */
export const getCurrentDateForInput = (): string => {
  return moment().format('YYYY-MM-DD');
};

/**
 * Get date placeholder for dd/MM/yyyy format
 * @returns Placeholder string for date input
 */
export const getDatePlaceholder = (): string => {
  return 'gg/aa/yyyy';
};

/**
 * Check if a date string is valid in dd/MM/yyyy format
 * @param dateString - Date string to validate
 * @returns True if date is valid, false otherwise
 */
export const isValidDateString = (dateString: string): boolean => {
  try {
    if (!dateString) return false;
    
    const momentDate = moment(dateString, 'DD/MM/YYYY', true);
    return momentDate.isValid();
  } catch (error) {
    return false;
  }
};

/**
 * Convert date from any format to dd/MM/yyyy display format
 * @param date - Date in any valid format
 * @returns Formatted date string in dd/MM/yyyy format
 */
export const convertToDisplayFormat = (date: string | Date | moment.Moment): string => {
  try {
    if (!date) return '';
    
    const momentDate = moment(date);
    if (!momentDate.isValid()) return '';
    
    return momentDate.format('DD/MM/YYYY');
  } catch (error) {
    console.warn('Moment date conversion error:', error);
    return '';
  }
};

/**
 * Convert date from any format to YYYY-MM-DD input format
 * @param date - Date in any valid format
 * @returns Formatted date string in YYYY-MM-DD format
 */
export const convertToInputFormat = (date: string | Date | moment.Moment): string => {
  try {
    if (!date) return '';
    
    const momentDate = moment(date);
    if (!momentDate.isValid()) return '';
    
    return momentDate.format('YYYY-MM-DD');
  } catch (error) {
    console.warn('Moment date input conversion error:', error);
    return '';
  }
};
